import axios, { AxiosError } from 'axios';
import { config } from '../config';

// Create axios instance
const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('atm_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('atm_auth_token');
      localStorage.removeItem('atm_user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('atm_auth_token');
    localStorage.removeItem('atm_user_data');
  }
};

// Notice API
export const noticeAPI = {
  getAll: async (status?: string) => {
    const response = await api.get('/notices', {
      params: status ? { status } : {}
    });
    return response.data;
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/notices/${id}`);
    return response.data;
  },
  
  create: async (noticeData: any) => {
    const response = await api.post('/notices', noticeData);
    return response.data;
  },
  
  update: async (id: string, noticeData: any) => {
    const response = await api.put(`/notices/${id}`, noticeData);
    return response.data;
  },
  
  activate: async (id: string) => {
    const response = await api.put(`/notices/${id}/activate`);
    return response.data;
  },
  
  cancel: async (id: string, reason?: string) => {
    const response = await api.put(`/notices/${id}/cancel`, { reason });
    return response.data;
  },
  
  complete: async (id: string, salesData: any) => {
    const response = await api.put(`/notices/${id}/complete`, salesData);
    return response.data;
  },
  
  getHistory: async (id: string) => {
    const response = await api.get(`/notices/${id}/history`);
    return response.data;
  }
};

// Dashboard API
export const dashboardAPI = {
  getSummary: async () => {
    const response = await api.get('/dashboard');
    return response.data;
  }
};

// Users API
export const userAPI = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  
  create: async (userData: { email: string; password: string; first_name: string; last_name: string; role: string }) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  
  update: async (id: string, userData: any) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  
  deactivate: async (id: string) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }
};

// Recipients API (Novus Capital contacts)
export const recipientAPI = {
  getAll: async (includeInactive: boolean = false) => {
    const response = await api.get('/recipients', {
      params: { includeInactive: includeInactive ? 'true' : 'false' }
    });
    return response.data;
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/recipients/${id}`);
    return response.data;
  },
  
  create: async (recipientData: { email: string; name?: string }) => {
    const response = await api.post('/recipients', recipientData);
    return response.data;
  },
  
  update: async (id: string, recipientData: { email?: string; name?: string; is_active?: boolean }) => {
    const response = await api.put(`/recipients/${id}`, recipientData);
    return response.data;
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/recipients/${id}`);
    return response.data;
  }
};

// External API (token-based, for Novus portal)
export const externalAPI = {
  getNotice: async (token: string) => {
    const response = await api.get(`/external/${token}`);
    return response.data;
  },
  
  activate: async (token: string) => {
    const response = await api.put(`/external/${token}/activate`);
    return response.data;
  },
  
  complete: async (token: string, salesData: { shares_sold: number; average_sell_price?: number; trade_notes?: string }) => {
    const response = await api.put(`/external/${token}/complete`, salesData);
    return response.data;
  }
};

// Export API
export const exportAPI = {
  // Download notices as CSV
  downloadNotices: async (params?: { status?: string; startDate?: string; endDate?: string }) => {
    const response = await api.get('/export/notices', {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    });
    downloadBlob(response.data, `notices-export-${new Date().toISOString().split('T')[0]}.csv`);
  },
  
  // Download audit trail as CSV
  downloadAudit: async (params?: { noticeId?: string; startDate?: string; endDate?: string }) => {
    const response = await api.get('/export/audit', {
      params,
      responseType: 'blob'
    });
    downloadBlob(response.data, `audit-trail-${new Date().toISOString().split('T')[0]}.csv`);
  },
  
  // Download monthly report as CSV
  downloadMonthlyReport: async (year: number, month: number) => {
    const response = await api.get('/export/monthly-report', {
      params: { year, month },
      responseType: 'blob'
    });
    downloadBlob(response.data, `monthly-report-${year}-${String(month).padStart(2, '0')}.csv`);
  }
};

// Helper to download blob
function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default api;

